export const unique = <T>(array: T[]) => Array.from(new Set<T>(array));

/**
 * Binary search to find insertion index for value in sorted array.
 */
export function sortedIndex<T>(array: T[], value: T): number {
  let low = 0;
  let high = array.length;

  while (low < high) {
    const mid = (low + high) >>> 1;
    if (array[mid] < value) {
      low = mid + 1;
    }
    else {
      high = mid;
    }
  }

  return low;
}

/**
 * Binary search to find the first index of value in sorted array.
 * Returns -1 if not found.
 */
export function sortedIndexOf<T>(array: T[], value: T): number {
  const index = sortedIndex(array, value);
  return index < array.length && array[index] === value ? index : -1;
}

/**
 * Binary search to find the last index of value in sorted array.
 * Returns -1 if not found.
 */
export function sortedLastIndexOf<T>(array: T[], value: T): number {
  let low = 0;
  let high = array.length;

  while (low < high) {
    const mid = (low + high) >>> 1;
    if (array[mid] <= value) {
      low = mid + 1;
    }
    else {
      high = mid;
    }
  }

  const index = low - 1;
  return index >= 0 && array[index] === value ? index : -1;
}

/**
 * Binary search with iteratee
 */
export function sortedIndexBy<T, U>(array: readonly T[], value: T, iteratee: (value: T) => U): number {
  const computedValue = iteratee(value);
  let low = 0;
  let high = array.length;

  while (low < high) {
    const mid = (low + high) >>> 1;
    if (iteratee(array[mid]) < computedValue) {
      low = mid + 1;
    }
    else {
      high = mid;
    }
  }

  return low;
}

/**
 * Adapted from https://stackoverflow.com/a/21822316
 *
 * Use for sorted arrays that are keyed by more than one factor/value.
 * @param array The sorted array to inspect.
 * @param value The value to evaluate.
 * @param iteratee The comparator (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort#description)
 * invoked between two elements.
 * @param retHighest if true, return the index of the last entry if there multiple equal entries.
 * Otherwise return the first (i.e. earliest) index.
 * @returns The index at which `value` should be inserted into `array`.
 */
const sortedIndexCompareBase = <T>(array: T[], value: T, comparator: (a: T, b: T) => number, retHighest: boolean) => {
  let low = 0;
  let high = array.length;

  while (low < high) {
    const mid = (low + high) >>> 1;

    const compared = comparator(array[mid], value);
    if (retHighest ? compared <= 0 : compared < 0) { low = mid + 1; }
    else { high = mid; }
  }

  return low;
};
const sortedIndexCompare = <T>(array: T[], value: T, comparator: (a: T, b: T) => number) =>
  sortedIndexCompareBase(array, value, comparator, false);
const sortedLastIndexCompare = <T>(array: T[], value: T, comparator: (a: T, b: T) => number) =>
  sortedIndexCompareBase(array, value, comparator, true);

abstract class SortedArrayBase<K, V extends K = K> {
  /**
   * Internal array.
   * Tried extending Array but it forces any new arrays
   * produced by this class to extend this class.
   */
  protected _array: V[];
  public get array(): Pick<V[], 'pop' | 'shift' | Exclude<keyof readonly V[], 'indexOf' | 'includes'>> & {
    splice: (start: number, deleteCount?: number) => V[];
  } {
    return this._array;
  }

  public constructor(params: V[] = [], comparator?: (a: K, b: K) => number) {
    this._array = params.slice().sort(comparator);
  }

  public clear(): void {
    this._array = [];
  }

  public includes(searchElement: K): boolean {
    return this.indexOf(searchElement) !== -1;
  }

  public abstract indexOf(searchElement: K): number;
}

/**
 * Acts a bit like a map, a bit like an array.
 * `K` (key) is a subset of V (value). It should indicated the components of V
 * that can be used as a key.
 */
export class SortedArrayBy<K, V extends K = K> extends SortedArrayBase<K, V> {
  public constructor(
    private readonly comparator: (a: K, b: K) => number,
    ...params: V[]
  ) {
    super(params, comparator);
  }

  public add(item: V) {
    const array = this._array;
    array.splice(sortedLastIndexCompare(array, item, this.comparator), 0, item);
  }

  public delete(key: K) {
    const index = this.indexOf(key);

    if (index !== -1) {
      this._array.splice(index, 1);

      return true;
    }

    return false;
  }

  public getSortedIndex(searchElement: K) {
    return sortedIndexCompare(this._array, searchElement, this.comparator);
  }

  public getFirst(searchElement: K) {
    const index = this.indexOf(searchElement);

    return index === -1 ? undefined : this._array[index];
  }

  public getLast(searchElement: K) {
    const index = this.lastIndexOf(searchElement);

    return index === -1 ? undefined : this._array[index];
  }

  public lastIndexOf(searchElement: K): number {
    const array = this._array;

    const index = sortedLastIndexCompare(array, searchElement, this.comparator) - 1;

    return index >= 0 && index < array.length && this.comparator(array[index], searchElement) === 0
      ? index
      : -1;
  }

  // ----- Native Array method implementations -----

  public includes(searchElement: K): boolean {
    return this.indexOf(searchElement) !== -1;
  }

  public indexOf(searchElement: K): number {
    const array = this._array;

    const index = sortedIndexCompare(array, searchElement, this.comparator);

    return index < array.length && this.comparator(array[index], searchElement) === 0
      ? index
      : -1;
  }
}

export class SortedArray<T> extends SortedArrayBase<T> {
  public constructor(...params: T[]) {
    super(params);
  }

  public add(item: T) {
    const array = this._array;
    array.splice(sortedIndex(array, item), 0, item);
  }

  public indexOf(searchElement: T): number {
    return sortedIndexOf(this._array, searchElement);
  }

  public lastIndexOf(searchElement: T): number {
    return sortedLastIndexOf(this._array, searchElement);
  }
}

export function getRandomElement<T>(array: T[]): T {
  const randomIndex = Math.floor(Math.random() * array.length);
  return array[randomIndex];
}

/**
 * Uniform random element; `undefined` if the array is empty.
 * Prefer this over {@link getRandomElement} when the collection may be empty.
 */
export function pickRandom<T>(items: readonly T[]): T | undefined {
  if (items.length === 0) { return undefined; }
  return items[Math.floor(Math.random() * items.length)];
}

/** Partial Fisher–Yates: uniform random subset of size `count` (or all when count ≥ length). */
export function takeRandomSubset<T>(items: readonly T[], count: number): T[] {
  const n = Math.max(0, Math.min(count, items.length));
  if (n === 0) { return []; }

  const pool = items.slice();
  const picked: T[] = [];
  for (let i = 0; i < n; i++) {
    const j = i + Math.floor(Math.random() * (pool.length - i));
    const swap = pool[j];
    const keep = pool[i];
    if (swap === undefined || keep === undefined) { break; }
    pool[j] = keep;
    pool[i] = swap;
    picked.push(swap);
  }
  return picked;
}

/**
 * Narrows `unknown` to `number[]`, or throws with `fieldName` in the message.
 */
export function readNumberArray(value: unknown, fieldName: string): number[] {
  if (!Array.isArray(value) || !value.every((entry): entry is number => typeof entry === 'number')) {
    throw new Error(`${fieldName} must be a number array`);
  }
  return value;
}
